<template>
    <div>
        <div class="row">
            <div class="col-12">
            <router-link class="ml-1" to=""><div @click="$router.back()">Back</div></router-link>
            </div>
        </div>
        <div class="row" v-if="disk">
            <div class="col-lg-12 col-xl-7">
                <card :title="`${disk.vendor} ${disk.model} ${disk.serial}`">
                    <template v-slot:header-right>
                        <button @click="fetchCertificate" class="btn btn-sm btn-outline-info" :class="!isValid ? 'disabled' : ''">Certificate</button>
                    </template>
                    <div class="card-body">
                        <small class="ml-2">Checksum {{ disk.hash }}</small>
                        <table class="table">
                            <tbody>
                                <tr>
                                    <th>Computer</th>
                                    <td>
                                        <router-link :to="`/erased/computers/${disk.hardware_id}`" v-if="hasHardware">
                                            <font-awesome-icon icon="desktop" />
                                        </router-link>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Computer serial</th>
                                    <td>{{ disk.system_serial }}</td>
                                </tr>
                                <tr>
                                    <th>Created at</th>
                                    <td>{{ disk.created_at | prettyDateTime }}</td>
                                </tr>
                                <tr>
                                    <th>Updated at</th>
                                    <td>{{ disk.updated_at | prettyDateTime }}</td>
                                </tr>
                                <tr>
                                    <th>Uploaded at</th>
                                    <td>{{ disk.uploaded_at | prettyDateTime }}</td>
                                </tr>
                                <tr>
                                    <th>Report revision</th>
                                    <td>{{ disk.revision }}</td>
                                </tr>
                                <tr>
                                    <th>Software version</th>
                                    <td>{{ disk.software_version }}</td>
                                </tr>
                                <tr>
                                    <th>Display name</th>
                                    <td>{{ disk.display_name }}</td>
                                </tr>
                                <tr>
                                    <th>Batch name</th>
                                    <td>{{ disk.batch_name }}</td>
                                </tr>
                                <tr>
                                    <th>Report name</th>
                                    <td>{{ disk.report_id }}</td>
                                </tr>
                                <tr>
                                    <th>Vendor</th>
                                    <td>{{ disk.vendor }}</td>
                                </tr>
                                <tr>
                                    <th>Model</th>
                                    <td>{{ disk.model }}</td>
                                </tr>
                                <tr>
                                    <th>Serial</th>
                                    <td>{{ disk.serial }}</td>
                                </tr>
                                <tr>
                                    <th>Firmware version</th>
                                    <td>{{ disk.firmware_version }}</td>
                                </tr>
                                <tr>
                                    <th>Sector size bytes</th>
                                    <td>{{ disk.sector_size_bytes }}</td>
                                </tr>
                                <tr>
                                    <th>Accessible number of sectors</th>
                                    <td>{{ disk.accessible_number_of_sectors }}</td>
                                </tr>
                                <tr>
                                    <th>Real number of sectors</th>
                                    <td>{{ disk.real_number_of_sectors }}</td>
                                </tr>
                                <tr>
                                    <th>Capacity</th>
                                    <td>{{ disk.capacity_display }}</td>
                                </tr>
                                <tr>
                                    <th>Protocol</th>
                                    <td>{{ disk.type_display }}</td>
                                </tr>
                                <tr>
                                    <th>Transport</th>
                                    <td>{{ disk.transport }}</td>
                                </tr>
                                <tr>
                                    <th>Medium</th>
                                    <td>{{ disk.medium_display }}</td>
                                </tr>
                                <tr>
                                    <th>Rotation rate</th>
                                    <td>{{ disk.rotation_rate }}</td>
                                </tr>
                                <!-- Test results -->
                                <tr>
                                    <th>SMART overall health</th>
                                    <td>{{ disk.smart_overall_health_display }}</td>
                                </tr>
                                <tr>
                                    <th>SMART short test result</th>
                                    <td>{{ disk.smart_short_test_result }}</td>
                                </tr>
                                <tr>
                                    <th>Power on hours</th>
                                    <td>{{ disk.power_on_hours }}</td>
                                </tr>
                                <tr>
                                    <th>Power cycles</th>
                                    <td>{{ disk.power_cycles }}</td>
                                </tr>
                                <tr>
                                    <th>Temperature celsius</th>
                                    <td>{{ disk.temperature_celsius }}</td>
                                </tr>
                                <tr>
                                    <th>Health</th>
                                    <td>{{ disk.health }}</td>
                                </tr>
                                <tr>
                                    <th>Reallocated sectors</th>
                                    <td>{{ disk.reallocated_sector_count }}</td>
                                </tr>
                                <tr>
                                    <th>Reallocated sectors after</th>
                                    <td>{{ disk.reallocated_sector_count_after }}</td>
                                </tr>
                                <!-- Erase results -->
                                <tr>
                                    <th>Status</th>
                                    <td>{{ disk.status_display }}</td>
                                </tr>
                                <tr>
                                    <th>Erase result</th>
                                    <td>{{ disk.erase_result_display }}</td>
                                </tr>
                                <tr>
                                    <th>Erase level</th>
                                    <td>{{ disk.erase_level }}</td>
                                </tr>
                                <tr>
                                    <th>Error</th>
                                    <td>{{ disk.error }}</td>
                                </tr>
                                <tr>
                                    <th>Erasure method</th>
                                    <td>{{ disk.erase_algorithm_display }}</td>
                                </tr>
                                <tr>
                                    <th>Erase start time</th>
                                    <td v-if="disk.erase_start_time">{{ disk.erase_start_time | prettyDateTime }}</td>
                                    <td v-else></td>
                                </tr>
                                <tr>
                                    <th>Erase finish time</th>
                                    <td v-if="disk.erase_finish_time">{{ disk.erase_finish_time | prettyDateTime }}</td>
                                    <td v-else></td>
                                </tr>
                                <tr>
                                    <th>Erase duration</th>
                                    <td>{{ disk.erase_duration_display }}</td>
                                </tr>
                                <tr>
                                    <th>Hidden areas</th>
                                    <td>{{ disk.hidden_areas_display }}</td>
                                </tr>
                                <tr>
                                    <th>Erase read speed</th>
                                    <td>{{ disk.erase_read_bytes_per_second_display }}</td>
                                </tr>
                                <tr>
                                    <th>Erase write speed</th>
                                    <td>{{ disk.erase_write_bytes_per_second_display }}</td>
                                </tr>
                                <tr>
                                    <th>Erase verified number of sectors</th>
                                    <td>{{ disk.erase_verified_number_of_sectors }}</td>
                                </tr>

                            </tbody>
                        </table>
                    </div>
                </card>
            </div>
            <div class="col-lg-12 col-xl-5">
                <toggle-card :title="`Extra (${disk.extra.length || 0})`" showInitially>
                    <button v-if="isLocked" class="btn btn-sm btn-outline-info float-right mb-1" type="button"
                        @click="unlock()">Edit</button>
                    <b-table v-if="isLocked" thead-class="d-none" show-empty small :items="disk.extra"
                        :fields="[{ key: 'key', isRowHeader: true }, 'value']"></b-table>
                    <extras-form v-else @close="lock()" @save="saveExtras" :items="disk.extra"></extras-form>
                </toggle-card>
                <toggle-card :title="`Log (${disk.log.length || 0})`" showInitially>
                    <b-table show-empty small :items="disk.log" :fields="['level', 'message']">
                        <template v-slot:cell(level)="row">
                            <div v-if="row.item.level == 0">DEBUG</div>
                            <div v-if="row.item.level == 1">INFO</div>
                            <div v-if="row.item.level == 2">WARNING</div>
                            <div v-if="row.item.level == 3">ERROR</div>
                        </template>
                    </b-table>
                </toggle-card>
            </div>
        </div>
        <div class="row" v-else>
            <div class="col-11">
                <card title="Disk" subTitle="404 error" :isFullPage="true">
                    <div class="card-body text-center">
                        <p>No disk was found at this url</p>
                    </div>
                </card>
            </div>
        </div>
    </div>
</template>

<script>
import { notify } from '../services/notify';

import api from '../services/api';
import ToggleCard from '../components/Cards/ToggleCard.vue';
import ExtrasForm from '../components/Forms/ExtrasForm.vue';

export default {
    name: "DiskErased",
    components: {
        ExtrasForm,
        ToggleCard,
    },
    props: {
        device_id: String,
    },
    data() {
        return {
            disk: null,
            isLoading: false,
            isLocked: true,
        };
    },
    async created() {
        this.isLoading = true;
        try {
            const response = await api.get(`/disk-devices/${this.device_id}`);
            this.disk = response.data;
        }
        catch(err) {
            notify().error('Error fetching report.');
        }
        finally {
            this.isLoading = false;
        }
    },
    methods: {
        async unlock() {
            this.isLocked = false;
        },
        async lock() {
            this.isLocked = true;
        },
        async saveExtras(extras) {
            this.isLoading = true;
            try {
                const response = await api.put(`/disk-devices/${this.device_id}`, extras);
                this.disk = response.data;
            }
            catch(err) {
                notify().error('Error saving report.');
            }
            finally {
                this.isLoading = false;
            }
        },
        async fetchCertificate() {
            this.isLoading = true;
            try {
                const response = await api.postJSON('/disk-devices/export', {
                    type: 'certificate',
                    selection: [this.disk.device_id],
                    filename: `${this.disk.report_id}.pdf`,
                }, { responseType: 'blob' });
                const url = window.URL.createObjectURL(response.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${this.disk.report_id}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            }
            catch(err) {
                notify().error('Error fetching certificate.');
            }
            finally {
                this.isLoading = false;
            }
        },
    },
    computed: {
        hasHardware() {
            return this.disk.hardware_id !== null;
        },
        isValid() {
            return !(this.disk.hash == null || this.disk.hash == "" || this.disk.hash == "INVALID");
        },
    }
};
</script>
